import { WebsiteComposite, WebsiteCreation, WebsiteStatus } from '@/services/Website/interfaces';
import { Roles } from '@/services/Roles';

export type ConsoleActionGroup = 'Create' | 'Manage' | 'Resources' | 'Activity' | 'Danger' | 'Navigate';

export type ConsoleActionRisk = 'low' | 'medium' | 'high' | 'destructive';

export type ConsoleActionId =
  | 'actions.list'
  | 'staging.create'
  | 'staging.merge'
  | 'alias.create'
  | 'subdomain.create'
  | 'wp.install'
  | 'wp.uninstall'
  | 'website.enable'
  | 'website.disable'
  | 'website.rename'
  | 'website.delete'
  | 'website.recreate'
  | 'website.retry'
  | 'dns.manage'
  | 'ssl.manage'
  | 'db.manage'
  | 'mail.manage'
  | 'ftp.manage'
  | 'vhost.manage'
  | 'redis.manage'
  | 'open.server'
  | 'open.group'
  | 'open.unix';

export interface ConsoleActionDefinition {
  id: ConsoleActionId;
  command: string;
  label: string;
  description: string;
  group: ConsoleActionGroup;
  risk: ConsoleActionRisk;
  scopes: string[];
  requiresContext: boolean;
  requireTypedDomain?: boolean;
  panel: string;
}

export interface ConsoleActionAvailability {
  action: ConsoleActionDefinition;
  available: boolean;
  disabledReason?: string;
}

export const CONSOLE_ACTIONS: ConsoleActionDefinition[] = [
  {
    id: 'actions.list',
    command: '/actions',
    label: 'List actions',
    description: 'Show available actions for the current context',
    group: 'Manage',
    risk: 'low',
    scopes: [Roles.WEBSITE_WEBSITE_READ],
    requiresContext: false,
    panel: 'actions-list',
  },
  {
    id: 'staging.create',
    command: '/staging create',
    label: 'Create staging',
    description: 'Create a staging environment for this website',
    group: 'Create',
    risk: 'medium',
    scopes: [Roles.WEBSITE_WEBSITE_CREATE],
    requiresContext: true,
    panel: 'staging-create',
  },
  {
    id: 'staging.merge',
    command: '/staging merge',
    label: 'Merge staging',
    description: 'Merge this staging site into its parent',
    group: 'Danger',
    risk: 'destructive',
    scopes: [Roles.WEBSITE_WEBSITE_CREATE],
    requiresContext: true,
    requireTypedDomain: true,
    panel: 'staging-merge',
  },
  {
    id: 'alias.create',
    command: '/alias create',
    label: 'Create alias',
    description: 'Create a domain alias',
    group: 'Create',
    risk: 'medium',
    scopes: [Roles.WEBSITE_WEBSITE_CREATE],
    requiresContext: true,
    panel: 'alias-create',
  },
  {
    id: 'subdomain.create',
    command: '/subdomain create',
    label: 'Create subdomain',
    description: 'Create a subdomain website',
    group: 'Create',
    risk: 'medium',
    scopes: [Roles.WEBSITE_WEBSITE_CREATE],
    requiresContext: true,
    panel: 'subdomain-create',
  },
  {
    id: 'wp.install',
    command: '/wp install',
    label: 'Install WordPress',
    description: 'Install WordPress on this website',
    group: 'Manage',
    risk: 'medium',
    scopes: [Roles.WEBSITE_WEBSITE_CREATE],
    requiresContext: true,
    panel: 'wp-install',
  },
  {
    id: 'wp.uninstall',
    command: '/wp uninstall',
    label: 'Uninstall WordPress',
    description: 'Remove WordPress from this website',
    group: 'Danger',
    risk: 'destructive',
    scopes: [Roles.WEBSITE_WEBSITE_CREATE],
    requiresContext: true,
    requireTypedDomain: true,
    panel: 'wp-uninstall',
  },
  {
    id: 'website.enable',
    command: '/enable',
    label: 'Enable website',
    description: 'Set website status to ACTIVE',
    group: 'Manage',
    risk: 'medium',
    scopes: [Roles.WEBSITE_WEBSITE_CREATE],
    requiresContext: true,
    panel: 'toggle-website',
  },
  {
    id: 'website.disable',
    command: '/disable',
    label: 'Disable website',
    description: 'Set website status to DISABLED',
    group: 'Manage',
    risk: 'medium',
    scopes: [Roles.WEBSITE_WEBSITE_CREATE],
    requiresContext: true,
    panel: 'toggle-website',
  },
  {
    id: 'website.rename',
    command: '/rename',
    label: 'Rename website',
    description: 'Rename the group main website domain',
    group: 'Manage',
    risk: 'high',
    scopes: [Roles.WEBSITE_MIGRATIONS_READ],
    requiresContext: true,
    panel: 'rename-website',
  },
  {
    id: 'website.delete',
    command: '/delete',
    label: 'Delete website',
    description: 'Delete this website',
    group: 'Danger',
    risk: 'destructive',
    scopes: [Roles.WEBSITE_WEBSITE_CREATE],
    requiresContext: true,
    requireTypedDomain: true,
    panel: 'delete-website',
  },
  {
    id: 'website.recreate',
    command: '/recreate',
    label: 'Recreate website',
    description: 'Retry failed website creation',
    group: 'Activity',
    risk: 'medium',
    scopes: [Roles.WEBSITE_WEBSITE_CREATE],
    requiresContext: true,
    panel: 'recreate-website',
  },
  {
    id: 'website.retry',
    command: '/retry',
    label: 'Retry creation action',
    description: 'Retry a failed creation action',
    group: 'Activity',
    risk: 'medium',
    scopes: [Roles.WEBSITE_WEBSITE_CREATE],
    requiresContext: true,
    panel: 'retry-action',
  },
  {
    id: 'dns.manage',
    command: '/dns manage',
    label: 'Manage DNS',
    description: 'List, sync, and delete DNS records',
    group: 'Resources',
    risk: 'high',
    scopes: [Roles.DNS_ZONE_READ],
    requiresContext: true,
    panel: 'dns-manage',
  },
  {
    id: 'ssl.manage',
    command: '/ssl manage',
    label: 'Manage SSL',
    description: 'Inspect, renew, or delete certificates',
    group: 'Resources',
    risk: 'high',
    scopes: [Roles.SSL_CERT_READ],
    requiresContext: true,
    panel: 'ssl-manage',
  },
  {
    id: 'db.manage',
    command: '/db manage',
    label: 'Manage databases',
    description: 'Create, sync, and delete databases',
    group: 'Resources',
    risk: 'high',
    scopes: [Roles.DATABASE_USER_READ],
    requiresContext: true,
    panel: 'db-manage',
  },
  {
    id: 'mail.manage',
    command: '/mail manage',
    label: 'Manage mailboxes',
    description: 'Activate and manage mailbox users',
    group: 'Resources',
    risk: 'high',
    scopes: [Roles.MAILBOX_MAILBOX_READ],
    requiresContext: true,
    panel: 'mail-manage',
  },
  {
    id: 'ftp.manage',
    command: '/ftp manage',
    label: 'Manage FTP users',
    description: 'Create, edit, and delete FTP users',
    group: 'Resources',
    risk: 'high',
    scopes: [Roles.FTP_USER_FTP_USER_READ],
    requiresContext: true,
    panel: 'ftp-manage',
  },
  {
    id: 'vhost.manage',
    command: '/vhost manage',
    label: 'Manage vhost',
    description: 'Edit aliases, PHP version, and wildcards',
    group: 'Resources',
    risk: 'high',
    scopes: [Roles.VHOST_VHOST_READ],
    requiresContext: true,
    panel: 'vhost-manage',
  },
  {
    id: 'redis.manage',
    command: '/redis manage',
    label: 'Manage Redis',
    description: 'Enable, disable, or sync Redis cache',
    group: 'Resources',
    risk: 'medium',
    scopes: [Roles.WEBSITE_WEBSITE_READ],
    requiresContext: true,
    panel: 'redis-manage',
  },
  {
    id: 'open.server',
    command: '/open server',
    label: 'Open server',
    description: 'Navigate to the origin server page',
    group: 'Navigate',
    risk: 'low',
    scopes: [Roles.SERVER_SERVER_READ],
    requiresContext: true,
    panel: '',
  },
  {
    id: 'open.group',
    command: '/open group',
    label: 'Open group',
    description: 'Navigate to the website group page',
    group: 'Navigate',
    risk: 'low',
    scopes: [Roles.WEBSITE_WEBSITE_READ],
    requiresContext: true,
    panel: '',
  },
  {
    id: 'open.unix',
    command: '/open unix',
    label: 'Open unix user',
    description: 'Navigate to the system user edit page',
    group: 'Navigate',
    risk: 'low',
    scopes: [Roles.SYSTEM_USER_UPDATE],
    requiresContext: true,
    panel: '',
  },
];

export function getConsoleAction(id: ConsoleActionId): ConsoleActionDefinition | undefined {
  return CONSOLE_ACTIONS.find((action) => action.id === id);
}

export function getConsoleActionByCommand(command: string): ConsoleActionDefinition | undefined {
  const normalized = command.trim().toLowerCase().replace(/\s+/g, ' ');
  return CONSOLE_ACTIONS.find((action) => action.command === normalized);
}

function hasScopes(userScopes: string[], required: string[]): boolean {
  if (!required.length) {
    return true;
  }
  return required.every((scope) => userScopes.indexOf(scope) !== -1);
}

function isAlias(composite: WebsiteComposite): boolean {
  return !!(composite.alias && composite.alias.parentId);
}

function isStaging(composite: WebsiteComposite): boolean {
  return !!(composite.staging && composite.staging.parentId);
}

function isGroupMain(composite: WebsiteComposite): boolean {
  return !!(composite.group && composite.group.mainWebsite === composite.website.id);
}

function canRetryAction(creations: WebsiteCreation[] | undefined): {
  ok: boolean;
  creationId?: number;
  actionId?: number;
  actionName?: string;
  reason?: string;
} {
  if (!creations || !creations.length) {
    return { ok: false, reason: 'No creation records found.' };
  }
  const newest = creations.slice().sort((a, b) => b.id - a.id)[0];
  const blocked = ['pending', 'in progress', 'retry', 'retrying', 'rolling back', 'done'];
  const candidate = (newest.actions || []).find((action) => {
    if (blocked.indexOf(action.status) !== -1) {
      return false;
    }
    const parents = action.parentActionsIds || [];
    return parents.every((parentId) => {
      const parent = (newest.actions || []).find((item) => item.id === parentId);
      return parent && parent.status === 'done';
    });
  });
  if (!candidate) {
    return { ok: false, reason: 'No retryable failed creation action.' };
  }
  return {
    ok: true,
    creationId: newest.id,
    actionId: candidate.id,
    actionName: candidate.action,
  };
}

export function evaluateActionAvailability(
  action: ConsoleActionDefinition,
  composite: WebsiteComposite | null,
  userScopes: string[],
  extras?: { creations?: WebsiteCreation[] },
): ConsoleActionAvailability {
  if (!hasScopes(userScopes, action.scopes)) {
    return {
      action,
      available: false,
      disabledReason: `Missing scope: ${action.scopes.join(', ')}`,
    };
  }

  if (action.id === 'actions.list') {
    return { action, available: true };
  }

  if (action.requiresContext && !composite) {
    return {
      action,
      available: false,
      disabledReason: 'Set context with /use <domain-or-id> first.',
    };
  }

  if (!composite) {
    return { action, available: true };
  }

  const status = composite.website.status;
  const alias = isAlias(composite);
  const staging = isStaging(composite);

  switch (action.id) {
    case 'staging.create':
      if (alias || staging) {
        return { action, available: false, disabledReason: 'Not available on alias or staging sites.' };
      }
      break;
    case 'staging.merge':
      if (!staging) {
        return { action, available: false, disabledReason: 'Only available on staging sites.' };
      }
      break;
    case 'alias.create':
    case 'subdomain.create':
    case 'wp.install':
      if (alias) {
        return { action, available: false, disabledReason: 'Not available on alias sites.' };
      }
      break;
    case 'website.enable':
      if (status === WebsiteStatus.ACTIVE) {
        return { action, available: false, disabledReason: 'Website is already ACTIVE.' };
      }
      if (status !== WebsiteStatus.DISABLED && status !== WebsiteStatus.ERROR) {
        return { action, available: false, disabledReason: `Cannot enable from status ${status}.` };
      }
      break;
    case 'website.disable':
      if (status === WebsiteStatus.DISABLED) {
        return { action, available: false, disabledReason: 'Website is already DISABLED.' };
      }
      if (status !== WebsiteStatus.ACTIVE) {
        return { action, available: false, disabledReason: `Cannot disable from status ${status}.` };
      }
      break;
    case 'website.rename':
      if (!isGroupMain(composite)) {
        return { action, available: false, disabledReason: 'Rename is limited to the group main website.' };
      }
      break;
    case 'website.recreate': {
      const creations = extras && extras.creations ? extras.creations : [];
      const newest = creations.slice().sort((a, b) => b.id - a.id)[0];
      if (!newest || newest.status !== 'failed') {
        return { action, available: false, disabledReason: 'Latest creation is not failed.' };
      }
      break;
    }
    case 'website.retry': {
      const retry = canRetryAction(extras && extras.creations);
      if (!retry.ok) {
        return { action, available: false, disabledReason: retry.reason };
      }
      break;
    }
    case 'redis.manage':
      // Panel itself gates mutations; opening is always allowed when scoped.
      break;
    case 'vhost.manage':
      if (composite.details.vhostId === null || composite.details.vhostId === undefined) {
        return { action, available: false, disabledReason: 'Website has no vhost.' };
      }
      break;
    case 'open.server':
      if (!composite.website.originServer) {
        return { action, available: false, disabledReason: 'No origin server.' };
      }
      break;
    case 'open.group':
      if (!composite.group) {
        return { action, available: false, disabledReason: 'Website has no group.' };
      }
      break;
    case 'open.unix':
      if (!composite.details.unixUserId) {
        return { action, available: false, disabledReason: 'Website has no unix user.' };
      }
      break;
    default:
      break;
  }

  return { action, available: true };
}

export function listAvailableActions(
  composite: WebsiteComposite | null,
  userScopes: string[],
  extras?: { creations?: WebsiteCreation[] },
): ConsoleActionAvailability[] {
  return CONSOLE_ACTIONS
    .filter((action) => action.id !== 'actions.list')
    .map((action) => evaluateActionAvailability(action, composite, userScopes, extras));
}

export function getRetryActionMeta(
  creations: WebsiteCreation[] | undefined,
): { creationId: number; actionId: number; actionName: string } | null {
  const retry = canRetryAction(creations);
  if (!retry.ok || retry.creationId === undefined || retry.actionId === undefined) {
    return null;
  }
  return {
    creationId: retry.creationId,
    actionId: retry.actionId,
    actionName: retry.actionName || 'action',
  };
}
